import type { AIModelChoices } from 'api';

const BASE_IGNORE_LABELS = [
  'Bale ID Tag',
  'bale_strap',
  'Background',
  'Non-Target',
  'Label',
  'Loose Material',
  'Scraps',
  'Unknown',
  'Bale Wire',
  'Boot',
];

// Coloured HDPE detections. The base hdpe / hdpe_non_natural stacks only ever emit
// "HDPE Jazz"; the hdpe_colour stack splits it into the eight named colours.
const HDPE_COLOUR_CATEGORIES = [
  'HDPE Jazz',
  'HDPE Green',
  'HDPE Blue',
  'HDPE Purple',
  'HDPE Red',
  'HDPE Orange',
  'HDPE Yellow',
  'HDPE Brown',
  'HDPE Black',
] as const;

const PET_BALED_CLEAR_LABELS = [
  'Clear Mouthwash Bottle',
  'PET Bottle Clear',
  'Very Large PET Bottle',
  'PET Bottle Large Clear',
] as const;
const PET_BALED_LIGHT_BLUE_LABELS = ['Light blue', 'PET Bottle Large Light Blue'] as const;
const PET_BALED_OTHER_IGNORE_LABELS = ['Bottle cap', 'Bottle Non-Food Grade Oil'] as const;

const PET_BALED_IGNORE_LABELS = [...PET_BALED_CLEAR_LABELS, ...PET_BALED_OTHER_IGNORE_LABELS] as const;
// pet_light_blue inverts the default: light-blue bottles dominate so hide them, surface clear bottles instead.
const PET_LIGHT_BLUE_IGNORE_LABELS = [...PET_BALED_LIGHT_BLUE_LABELS, ...PET_BALED_OTHER_IGNORE_LABELS] as const;

// Model-specific labels to ignore
const MODEL_SPECIFIC_IGNORE_LABELS: Record<AIModelChoices, readonly string[]> = {
  alu: ['Aluminium Beverage Cans'],
  alu_loose: [],
  briquette: [],
  hdpe: ['HDPE Natural'],
  hdpe_colour: ['HDPE Natural', ...HDPE_COLOUR_CATEGORIES],
  hdpe_non_natural: ['HDPE Natural'],
  other: [],
  pet: PET_BALED_IGNORE_LABELS,
  pet_dummy_bale: PET_BALED_IGNORE_LABELS,
  pet_dummy_bale_2: PET_BALED_IGNORE_LABELS,
  pet_envases: PET_BALED_IGNORE_LABELS,
  pet_light_blue: PET_LIGHT_BLUE_IGNORE_LABELS,
  pet_loose: [],
} as const;

export const getIgnoredLabels = (model: AIModelChoices): readonly string[] => {
  return [...BASE_IGNORE_LABELS, ...MODEL_SPECIFIC_IGNORE_LABELS[model]];
};

export const MODEL_FALLBACK_CATEGORIES: Record<AIModelChoices, string> = {
  alu: 'UBC',
  alu_loose: 'UBC',
  briquette: 'UBC',
  hdpe: 'Other',
  hdpe_colour: 'Other',
  hdpe_non_natural: 'Other',
  other: 'Other',
  pet: 'PET Bottle',
  pet_dummy_bale: 'PET Bottle',
  pet_dummy_bale_2: 'PET Bottle',
  pet_envases: 'PET Bottle',
  pet_light_blue: 'PET Bottle',
  pet_loose: 'PET Bottle',
};

export const EMPTY_HIDDEN_CATEGORIES = Object.freeze([] as const);

const createLabelGroup = (
  categories: readonly string[],
  options: { colourOverride?: string; ignoreDarkening?: boolean } = {}
): LabelGroupConfig => ({
  categories,
  colourOverride: options.colourOverride,
  ignoreDarkening: options.ignoreDarkening,
});

const ANALYSED_AREA_LABEL = 'Analysed Area';
const ANALYSED_AREA_GROUP = createLabelGroup(['Bale Polygon', 'Bale'], { ignoreDarkening: true });

export type LabelGroupConfig = {
  categories: readonly string[];
  colourOverride?: string;
  ignoreDarkening?: boolean;
};

export type LabelGroups = Record<string, LabelGroupConfig>;

const ALU_LABEL_GROUPS: LabelGroups = {
  [ANALYSED_AREA_LABEL]: ANALYSED_AREA_GROUP,
  'Aluminium Food Can': createLabelGroup(['Aluminium Food Can']),
  'Foil and Tray': createLabelGroup(['Aluminium Foil Trays']),
  'Non Metal': createLabelGroup([
    'Rigid Plastic',
    'Film',
    'Non Metallic',
    'Fibers & Textile',
    'Small Plastic',
    'Lighters',
    'Non-Metal',
    'WEEE',
  ]),
  'Other Aluminium': createLabelGroup([
    'Aluminium Bottles',
    'Aluminium Caps',
    'Aluminium Aerosol',
    'Other Aluminium',
    'Bottle cap',
  ]),
  'Other Metal': createLabelGroup(['Other Metal', 'Steel']),
  Prohibitive: createLabelGroup(['Vapes']),
};

const INSPECTION_ALU_LABEL_GROUPS: LabelGroups = {
  ...ALU_LABEL_GROUPS,
  'Aluminium Aerosol': createLabelGroup(['Aluminium Aerosol']),
  'Other Aluminium': createLabelGroup(['Aluminium Bottles', 'Aluminium Caps', 'Other Aluminium', 'Bottle cap']),
};

const PET_LABEL_GROUPS: LabelGroups = {
  [ANALYSED_AREA_LABEL]: ANALYSED_AREA_GROUP,
  Brown: createLabelGroup(['PET Bottle Brown'], { colourOverride: '#672D00' }),
  'Clear Contaminated PET Tray': createLabelGroup(['PET Tray Contaminated Clear'], { colourOverride: '#7BE0D2' }),
  'Clear Full Sleeve, non-food-grade': createLabelGroup(['Clear Full Sleeve Non-Food Grade'], {
    colourOverride: '#A89BB9',
  }),
  'Clear PET tray': createLabelGroup(['Clear tray'], { colourOverride: '#B9E2FF' }),
  'Clear, non-food-grade': createLabelGroup(['Clear Non-Food grade'], {
    colourOverride: '#C8C8C8',
  }),
  'Dark Blue': createLabelGroup(['Dark blue'], { colourOverride: '#2100E8' }),
  Film: createLabelGroup(['Film'], { colourOverride: '#D6FF77' }),
  'Full Sleeve Bottle': createLabelGroup(['PET Bottle Full Sleeve Clear'], { colourOverride: '#FF9DE8' }),
  Green: createLabelGroup(['Green'], { colourOverride: '#14BE14' }),
  'Light Blue': createLabelGroup(['Light blue', 'PET Bottle Large Light Blue'], { colourOverride: '#40FFFF' }),
  Liquid: createLabelGroup(['Liquids'], { colourOverride: '#B400B1' }),
  Metal: createLabelGroup(['Metal'], { colourOverride: '#E10600' }),
  'Non Plastic': createLabelGroup(['Non Plastic'], { colourOverride: '#FF891C' }),
  'Oil Bottle': createLabelGroup(['PET Bottle Oil Clear', 'PET Bottle Large Oil Clear'], { colourOverride: '#C18CFF' }),
  Opaque: createLabelGroup(['PET Bottle White', 'PET Bottle Opaque'], { colourOverride: '#009CE0' }),
  'Other Colour': createLabelGroup(['Other colour'], { colourOverride: '#FFF64A' }),
  'Other Plastic': createLabelGroup(['Other Plastic', 'Bottle cap'], { colourOverride: '#E6007A' }),
  'PET Bottle Clear': createLabelGroup(['PET Bottle Clear'], { colourOverride: '#005FCC' }),
  PP: createLabelGroup(['Polypropylene PP'], { colourOverride: '#FF6F61' }),
  Prohibitive: createLabelGroup(['Vapes'], { colourOverride: '#FF4800' }),
};

const HDPE_LABEL_GROUPS: LabelGroups = {
  [ANALYSED_AREA_LABEL]: ANALYSED_AREA_GROUP,
  'Expanded Polystyrene': createLabelGroup(['Expanded Polystyrene EPS'], { colourOverride: '#00BCD4' }),
  Film: createLabelGroup(['Film'], { colourOverride: '#D6FF77' }),
  'HDPE Coloured': createLabelGroup(HDPE_COLOUR_CATEGORIES, { colourOverride: '#1E90FF' }),
  Metal: createLabelGroup(['Metal'], { colourOverride: '#E10600' }),
  'Natural Non-Food Grade': createLabelGroup(['HDPE Natural Non-Food Grade'], { colourOverride: '#B45309' }),
  'Non Plastic': createLabelGroup(['Non Plastic'], { colourOverride: '#FF891C' }),
  'Other Colour': createLabelGroup(['Other colour'], { colourOverride: '#FFF64A' }),
  'Other Plastic': createLabelGroup(['Other Plastic'], { colourOverride: '#E6007A' }),
  'PET Bottle Clear': createLabelGroup(['PET Bottle Clear'], { colourOverride: '#005FCC' }),
  'PET Bottle Full Sleeve Clear': createLabelGroup(['PET Bottle Full Sleeve Clear'], { colourOverride: '#8B5CF6' }),
  'Silicon Tubes': createLabelGroup(['HDPE Silicon Tubes'], { colourOverride: '#00A86B' }),
  White: createLabelGroup(['HDPE White'], { colourOverride: '#FFFFFF' }),
  'White Non-Food Grade': createLabelGroup(['HDPE White Non-Food Grade'], { colourOverride: '#75A3FF' }),
};

export const LABEL_GROUPS: Record<AIModelChoices, LabelGroups> = {
  alu: ALU_LABEL_GROUPS,
  alu_loose: ALU_LABEL_GROUPS,
  briquette: ALU_LABEL_GROUPS,
  hdpe: HDPE_LABEL_GROUPS,
  hdpe_colour: HDPE_LABEL_GROUPS,
  hdpe_non_natural: HDPE_LABEL_GROUPS,
  other: PET_LABEL_GROUPS,
  pet: PET_LABEL_GROUPS,
  pet_dummy_bale: PET_LABEL_GROUPS,
  pet_dummy_bale_2: PET_LABEL_GROUPS,
  pet_envases: PET_LABEL_GROUPS,
  pet_light_blue: PET_LABEL_GROUPS,
  pet_loose: PET_LABEL_GROUPS,
};

export const getLabelGroups = ({
  showAerosols = false,
}: {
  showAerosols: boolean;
}): Record<AIModelChoices, LabelGroups> => {
  if (!showAerosols) {
    return LABEL_GROUPS;
  }

  return {
    alu: INSPECTION_ALU_LABEL_GROUPS,
    alu_loose: INSPECTION_ALU_LABEL_GROUPS,
    briquette: INSPECTION_ALU_LABEL_GROUPS,
    hdpe: HDPE_LABEL_GROUPS,
    hdpe_colour: HDPE_LABEL_GROUPS,
    hdpe_non_natural: HDPE_LABEL_GROUPS,
    other: PET_LABEL_GROUPS,
    pet: PET_LABEL_GROUPS,
    pet_dummy_bale: PET_LABEL_GROUPS,
    pet_dummy_bale_2: PET_LABEL_GROUPS,
    pet_envases: PET_LABEL_GROUPS,
    pet_light_blue: PET_LABEL_GROUPS,
    pet_loose: PET_LABEL_GROUPS,
  };
};

export const getLabelsWithReducedDarkening = ({
  model,
  showAerosols,
}: {
  model: AIModelChoices;
  showAerosols: boolean;
}): readonly string[] => {
  const modelGroups = getLabelGroups({ showAerosols })[model];
  return Object.values(modelGroups).flatMap((group) => (group.ignoreDarkening ? group.categories : []));
};
